import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Gift, AlertCircle, CreditCard, Wallet, FileText, Bell, ArrowRight, BarChart } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";

// Import our new components
import StripeCheckout from "@/components/subscription/StripeCheckout";
import CustomerPortal from "@/components/subscription/CustomerPortal";
import WalletManager from "@/components/subscription/WalletManager";
import PaymentMethods from "@/components/subscription/PaymentMethods";
import JournalistConnect from "@/components/subscription/JournalistConnect";
import SubscriptionAnalytics from "@/components/subscription/SubscriptionAnalytics";
import SubscriptionPlans from "@/components/subscription/SubscriptionPlans";
import { getSubscriptionStatus, getWalletBalance } from "@/services/stripeService";

const Subscription = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("plans");
  const [walletBalance, setWalletBalance] = useState(0);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isJournalist, setIsJournalist] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab && ['plans', 'wallet', 'payment-methods', 'active', 'analytics', 'connect', 'special', 'how-it-works'].includes(tab)) {
      setActiveTab(tab);
    }

    const checkStatus = async () => {
      setLoading(true);
      
      const subscriptionData = await getSubscriptionStatus();
      setIsSubscribed(subscriptionData.isSubscribed);
      
      const walletData = await getWalletBalance();
      setWalletBalance(walletData.balance);
      
      setIsJournalist(true);
      
      setLoading(false);
    };
    
    checkStatus();
    
    if (params.get('success') === 'true') {
      toast({
        title: "Payment Successful",
        description: "Thank you for your payment!",
      });
    } else if (params.get('canceled') === 'true') {
      toast({
        title: "Payment Canceled",
        description: "Your payment process was canceled. You can try again anytime.",
        variant: "destructive"
      });
    }
  }, [location.search, toast]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate(`/subscription?tab=${value}`, { replace: true });
  };

  const handleWalletUpdated = (balance: number) => {
    setWalletBalance(balance);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-directpress-blue mb-3">Subscription & Payments</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Support quality journalism and enjoy unlimited access to your favorite writers and topics.
            </p>
          </div>

          <Tabs defaultValue="plans" value={activeTab} onValueChange={handleTabChange} className="mb-12">
            <TabsList className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 w-full mb-6">
              <TabsTrigger value="plans">Plans</TabsTrigger>
              <TabsTrigger value="active">My Subscription</TabsTrigger>
              <TabsTrigger value="wallet">Wallet</TabsTrigger>
              <TabsTrigger value="payment-methods">Payment Methods</TabsTrigger>
              {isJournalist && (
                <TabsTrigger value="connect">Journalist Payments</TabsTrigger>
              )}
              {isJournalist && (
                <TabsTrigger value="analytics">Analytics</TabsTrigger>
              )}
              <TabsTrigger value="special">Special Offers</TabsTrigger>
              <TabsTrigger value="how-it-works">How It Works</TabsTrigger>
            </TabsList>
            
            <TabsContent value="plans">
              <SubscriptionPlans />
            </TabsContent>
            
            <TabsContent value="active">
              <CustomerPortal />
            </TabsContent>
            
            <TabsContent value="wallet">
              <WalletManager onWalletUpdated={handleWalletUpdated} />
            </TabsContent>
            
            <TabsContent value="payment-methods">
              <PaymentMethods />
            </TabsContent>
            
            {isJournalist && (
              <TabsContent value="connect">
                <JournalistConnect />
              </TabsContent>
            )}
            
            {isJournalist && (
              <TabsContent value="analytics">
                <SubscriptionAnalytics />
              </TabsContent>
            )}
            
            <TabsContent value="special">
              <Card>
                <CardHeader>
                  <CardTitle>Special Offers</CardTitle>
                  <CardDescription>Exclusive deals and promotions for DirectPress users</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                    <h3 className="font-medium text-lg mb-2">Annual Subscription Deal</h3>
                    <p className="text-muted-foreground mb-3">
                      Get 2 months free when you switch to an annual subscription plan.
                    </p>
                    <Button variant="outline">Learn More</Button>
                  </div>
                  
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
                    <h3 className="font-medium text-lg mb-2">Refer a Friend</h3>
                    <p className="text-muted-foreground mb-3">
                      Get $5 in wallet credit when you refer a friend to DirectPress.
                    </p>
                    <Button variant="outline">Invite Friends</Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="how-it-works">
              <Card>
                <CardHeader>
                  <CardTitle>How Payments Work on DirectPress</CardTitle>
                  <CardDescription>Understanding our payment system and revenue distribution</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="font-medium text-lg">For Readers</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="border rounded-lg p-4">
                        <div className="bg-primary/10 p-2 rounded-full w-10 h-10 flex items-center justify-center mb-3">
                          <span className="font-bold text-primary">1</span>
                        </div>
                        <h4 className="font-medium mb-2">Choose Your Plan</h4>
                        <p className="text-sm text-muted-foreground">
                          Subscribe to DirectPress Premium or support individual journalists directly.
                        </p>
                      </div>
                      
                      <div className="border rounded-lg p-4">
                        <div className="bg-primary/10 p-2 rounded-full w-10 h-10 flex items-center justify-center mb-3">
                          <span className="font-bold text-primary">2</span>
                        </div>
                        <h4 className="font-medium mb-2">Secure Payment</h4>
                        <p className="text-sm text-muted-foreground">
                          All payments are securely processed via Stripe with industry-standard encryption.
                        </p>
                      </div>
                      
                      <div className="border rounded-lg p-4">
                        <div className="bg-primary/10 p-2 rounded-full w-10 h-10 flex items-center justify-center mb-3">
                          <span className="font-bold text-primary">3</span>
                        </div>
                        <h4 className="font-medium mb-2">Enjoy Premium Content</h4>
                        <p className="text-sm text-muted-foreground">
                          Access all premium content or use micropayments for individual articles.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-4">
                    <h3 className="font-medium text-lg">For Journalists</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="border rounded-lg p-4">
                        <div className="bg-primary/10 p-2 rounded-full w-10 h-10 flex items-center justify-center mb-3">
                          <span className="font-bold text-primary">1</span>
                        </div>
                        <h4 className="font-medium mb-2">Connect Stripe Account</h4>
                        <p className="text-sm text-muted-foreground">
                          Set up your Stripe Connect account to receive direct payments from readers.
                        </p>
                      </div>
                      
                      <div className="border rounded-lg p-4">
                        <div className="bg-primary/10 p-2 rounded-full w-10 h-10 flex items-center justify-center mb-3">
                          <span className="font-bold text-primary">2</span>
                        </div>
                        <h4 className="font-medium mb-2">Revenue Distribution</h4>
                        <p className="text-sm text-muted-foreground">
                          Earn 70% of subscription and micropayment revenue from your content.
                        </p>
                      </div>
                      
                      <div className="border rounded-lg p-4">
                        <div className="bg-primary/10 p-2 rounded-full w-10 h-10 flex items-center justify-center mb-3">
                          <span className="font-bold text-primary">3</span>
                        </div>
                        <h4 className="font-medium mb-2">Weekly Payouts</h4>
                        <p className="text-sm text-muted-foreground">
                          Receive automatic payouts directly to your bank account on a weekly basis.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mt-4">
                    <h4 className="font-medium mb-2">Secure Payment Processing</h4>
                    <p className="text-sm text-muted-foreground">
                      DirectPress uses Stripe for all payment processing. Your financial information is never stored on our servers 
                      and all transactions are encrypted using industry-standard security protocols.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          
          <div className="mt-16 bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-medium mb-3">Account Settings</h3>
            <Separator className="mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Auto-renewal</h4>
                  <p className="text-sm text-muted-foreground">
                    Automatically renew subscriptions when they expire
                  </p>
                </div>
                <div className="flex items-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" defaultChecked className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-directpress-blue"></div>
                  </label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Payment notifications</h4>
                  <p className="text-sm text-muted-foreground">
                    Receive notifications about payments and renewals
                  </p>
                </div>
                <div className="flex items-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" defaultChecked className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-directpress-blue"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
